import { decodeDisplayText, getUrlDisplayTitle } from '../../util/displayText';
import '../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useState } from 'react';
import URLPreviewPlugin from '../MessagePreview/Plugins/URLPreviewPlugin';
import styled from 'styled-components';
import { buildWorkspaceMediaGallery } from '../../util/workspaceMediaGallery';
import { FiBookOpen } from 'react-icons/fi';

import { PALE_WHITE_6, PRIMARY_COLOR_1, WHITE } from '../../appearance/Colors';
import ArticleReaderModal from '../ArticleReaderModal';
import {
    getNextMediaSource,
    getUrlMetaVideoSource,
    isUrlMetaVideo,
    resolveUrlMetaPreviewMedia
} from '../../util/url-meta-media';

const COMPACT_DESCRIPTION_LIMIT = 160;
const FULL_DESCRIPTION_LIMIT = 600;
const GALLERY_PREVIEW_LIMIT = 4;

const truncate = (text, limit) =>
    text && text.length > limit ? `${text.slice(0, limit).trim()}…` : text;

const getSourceLabel = (urlMeta, sourceHost) => {
    if (urlMeta?.publisher) {
        return urlMeta.publisher;
    }

    if (sourceHost) {
        return sourceHost;
    }

    if (!urlMeta?.url) {
        return '';
    }

    try {
        return new URL(urlMeta.url).hostname;
    } catch (error) {
        return urlMeta.url;
    }
};

/**
 * Single link-preview treatment for a message's urlMeta — used by
 * ReadOnlyMessageCard (compact), ReadOnlyMessageChat (full), and
 * ReadOnlyMessageDetailContent (full), which previously each had their own
 * ad hoc rendering of the same data. Covers article text, image galleries,
 * and attached video/audio — fields that were fetched but never shown
 * before (see READ_ONLY_URL_META_FIELDS in gql-operations/fragments).
 *
 * Deliberately does not render `urlMeta.embed` — that field carries raw
 * HTML plus a third-party <script src>, and loading arbitrary remote
 * scripts on an anonymous public page is a real risk this component isn't
 * trying to solve. Skip until that gets its own security review.
 */
const ReadOnlyUrlMetaPreview = props => {
    if (!props.urlMeta) return null;
    if (props.variant !== 'full') return <CompactReadOnlyUrlMetaPreview {...props} />;
    return <URLPreviewPlugin
        readOnly
        hideTranscriptAction
        index={0}
        metaData={props.urlMeta}
        message={{ urlMeta: [props.urlMeta], sourceHost: props.sourceHost }}
        onOpenVideoWorkspace={props.onOpenWorkspace ? (_, source) => props.onOpenWorkspace(source) : undefined}
    />;
};

const CompactReadOnlyUrlMetaPreview = ({ urlMeta, sourceHost, variant = 'compact', boxed = false, onOpenWorkspace }) => {
    const {t: lnT} = useTranslation();

    const [isArticleReaderOpen, setIsArticleReaderOpen] = useState(false);

    if (!urlMeta) {
        return null;
    }

    const isCompact = variant === 'compact';
    const sourceLabel = getSourceLabel(urlMeta, sourceHost);
    const descriptionText = decodeDisplayText(urlMeta.description || urlMeta.article_body);
    const description = truncate(descriptionText, isCompact ? COMPACT_DESCRIPTION_LIMIT : FULL_DESCRIPTION_LIMIT);
    const galleryImages = (urlMeta.gallery || []).slice(0, GALLERY_PREVIEW_LIMIT);
    const extraGalleryCount = (urlMeta.gallery || []).length - galleryImages.length;
    const videoSource = getUrlMetaVideoSource(urlMeta);
    const isVideo = isUrlMetaVideo(urlMeta);
    const isAudioOnly = !isVideo && urlMeta.media_has_audio && (urlMeta.media_cdn_url || urlMeta.media_url);
    const hasArticleBody = Boolean(String(urlMeta.article_body || '').trim());
    const previewMedia = React.useMemo(() => resolveUrlMetaPreviewMedia(urlMeta), [lnT, urlMeta]);
    const [compactImageSource, setCompactImageSource] = useState(previewMedia.primarySource);

    React.useEffect(() => {
        setCompactImageSource(previewMedia.primarySource);
    }, [previewMedia.primarySource]);

    const handleCompactImageError = () => {
        const nextSource = getNextMediaSource(compactImageSource, previewMedia.sourceChain);
        setCompactImageSource(nextSource);
    };

    if (!sourceLabel && !urlMeta.title && !description && galleryImages.length === 0 && !isVideo && !isAudioOnly) {
        return null;
    }

    if (isCompact) {
        const previewImage = compactImageSource;
        const hasMediaPreview = Boolean(previewImage || videoSource);

        return (
            <CompactUrlWrapper $boxed={boxed}>
                <CompactPreviewRow>
                    {hasMediaPreview ? (
                        <CompactImageSection>
                            <CompactMediaFrame $isAuthorImage={previewMedia.isAuthorImage}>
                                {isVideo && videoSource ? (
                                    <CompactVideo
                                        controls
                                        preload="metadata"
                                        poster={previewImage}
                                        onClick={event => event.stopPropagation()}
                                    >
                                        <source src={videoSource} />
                                    </CompactVideo>
                                ) : (
                                    <CompactImage
                                        src={previewImage}
                                        alt={urlMeta.title || lnT('folders:preview')}
                                        loading="lazy"
                                        $isAuthorImage={previewMedia.isAuthorImage}
                                        onError={handleCompactImageError}
                                    />
                                )}
                            </CompactMediaFrame>
                        </CompactImageSection>
                    ) : null}

                    <CompactContentSection>
                        <CompactUrlSection>
                            {urlMeta.title ? <CompactTitle>{getUrlDisplayTitle(urlMeta.title)}</CompactTitle> : null}
                            {description ? <CompactDescription>{description}</CompactDescription> : null}
                        </CompactUrlSection>

                        {(sourceLabel || urlMeta.author || urlMeta.url) ? (
                            <CompactFooterRow>
                                {urlMeta.logo ? <CompactLogo src={urlMeta.logo} alt="" loading="lazy" /> : null}
                                <CompactFooterText>
                                    {urlMeta.author ? lnT('common:text_ae6ed6c2', { value1: String(urlMeta.author), value2: String(sourceLabel ? ` on ${sourceLabel}` : '') }) : sourceLabel || urlMeta.url}
                                </CompactFooterText>
                            </CompactFooterRow>
                        ) : null}

                        {hasArticleBody ? (
                            <CompactReadButton
                                type="button"
                                onClick={event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    if (onOpenWorkspace) {
                                        onOpenWorkspace({ type: 'article', articleBody: urlMeta.article_body, title: urlMeta.title, url: urlMeta.url });
                                    } else {
                                        setIsArticleReaderOpen(true);
                                    }
                                }}
                            >
                                <FiBookOpen size={15} />
                                <span>{lnT('messages:read')}</span>
                            </CompactReadButton>
                        ) : null}
                    </CompactContentSection>
                </CompactPreviewRow>

                {hasArticleBody ? (
                    <ArticleReaderModal
                        isOpen={isArticleReaderOpen}
                        onRequestClose={() => setIsArticleReaderOpen(false)}
                        title={urlMeta.title}
                        articleBody={urlMeta.article_body}
                    />
                ) : null}
            </CompactUrlWrapper>
        );
    }

    return (
        <Preview $boxed={boxed}>
            {(urlMeta.author || sourceLabel) ? (
                <ByLine>
                    {urlMeta.logo ? <PublisherIcon src={urlMeta.logo} alt="" loading="lazy" /> : null}
                    {urlMeta.author ? <AuthorName>{urlMeta.author}</AuthorName> : null}
                    {urlMeta.author && sourceLabel ? <ByLineSeparator>·</ByLineSeparator> : null}
                    {sourceLabel ? <SourceLabel>{sourceLabel}</SourceLabel> : null}
                </ByLine>
            ) : null}

            {urlMeta.title ? <Title>{getUrlDisplayTitle(urlMeta.title)}</Title> : null}
            {description ? <Description $isCompact={isCompact}>{description}</Description> : null}

            {isVideo ? (
                <MediaVideo
                    controls
                    preload="metadata"
                    poster={urlMeta.image_cdn_url || urlMeta.image}
                    onClick={event => event.stopPropagation()}
                >
                    <source src={videoSource} />
                </MediaVideo>
            ) : isAudioOnly ? (
                <MediaAudio controls preload="metadata" onClick={event => event.stopPropagation()}>
                    <source src={urlMeta.media_cdn_url || urlMeta.media_url} />
                </MediaAudio>
            ) : null}

            {onOpenWorkspace && isVideo ? (
                <ReadButton type="button" onClick={() => onOpenWorkspace({ type: 'video', src: videoSource, originalUrl: urlMeta.url })}>
                    {lnT('messages:playVideo')}
                </ReadButton>
            ) : null}
            {!isCompact && galleryImages.length > 0 ? (
                <GalleryGrid>
                    {galleryImages.map((slide, index) => (
                        <GalleryItem
                            key={slide.image || slide.media_url || index}
                            as={onOpenWorkspace ? 'button' : 'div'}
                            type={onOpenWorkspace ? 'button' : undefined}
                            aria-label={onOpenWorkspace ? lnT('messages:openImage', { value1: String(index + 1) }) : undefined}
                            onClick={onOpenWorkspace ? () => onOpenWorkspace({
                                type: 'image',
                                gallery: buildWorkspaceMediaGallery(urlMeta.gallery, { originalUrl: urlMeta.url, title: urlMeta.title }),
                                initialIndex: index
                            }) : undefined}
                        >
                            <GalleryImage src={slide.image} alt="" loading="lazy" />
                            {index === galleryImages.length - 1 && extraGalleryCount > 0 ? (
                                <GalleryMoreOverlay>+{extraGalleryCount}</GalleryMoreOverlay>
                            ) : null}
                        </GalleryItem>
                    ))}
                </GalleryGrid>
            ) : null}

            {hasArticleBody ? (
                <ReadButton
                    type="button"
                    onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (onOpenWorkspace) {
                            onOpenWorkspace({ type: 'article', articleBody: urlMeta.article_body, title: urlMeta.title, url: urlMeta.url });
                        } else {
                            setIsArticleReaderOpen(true);
                        }
                    }}
                >
                    <FiBookOpen size={14} />
                    <span>{lnT('messages:read')}</span>
                </ReadButton>
            ) : null}

            {hasArticleBody ? (
                <ArticleReaderModal
                    isOpen={isArticleReaderOpen}
                    onRequestClose={() => setIsArticleReaderOpen(false)}
                    title={urlMeta.title}
                    articleBody={urlMeta.article_body}
                />
            ) : null}
        </Preview>
    );
};

const Preview = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;

    ${({ $boxed }) =>
        $boxed &&
        `
        padding: 10px;
        border-radius: 8px;
        background: rgba(0, 0, 0, 0.2);
        border: 1px solid rgba(255, 255, 255, 0.08);
    `}
`;

const CompactUrlWrapper = styled.div`
    display: flex;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    box-sizing: border-box;
`;

const CompactPreviewRow = styled.div`
    display: flex;
    flex-direction: row;
    gap: 10px;
    width: 100%;
    min-width: 0;
    align-items: flex-start;
`;

const CompactImageSection = styled.div`
    flex: 0 0 auto;
    overflow: hidden;
    max-width: 96px;
`;

const CompactMediaFrame = styled.div`
    position: relative;
    width: ${({ $isAuthorImage }) => ($isAuthorImage ? '40px' : '96px')};
    height: ${({ $isAuthorImage }) => ($isAuthorImage ? '40px' : '72px')};
    border-radius: ${({ $isAuthorImage }) => ($isAuthorImage ? '50%' : '6px')};
    overflow: hidden;
    background-color: ${PRIMARY_COLOR_1};
    flex: 0 0 auto;
`;

const CompactImage = styled.img`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border-radius: inherit;
    object-fit: ${({ $isAuthorImage }) => ($isAuthorImage ? 'cover' : 'contain')};
    display: block;
`;

const CompactVideo = styled.video`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border-radius: inherit;
    object-fit: contain;
    display: block;
    background: #000000;
`;

const CompactContentSection = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    justify-content: center;
`;

const CompactUrlSection = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
    word-break: break-word;
    overflow-wrap: break-word;
`;

const CompactTitle = styled.div`
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    font-size: 13px;
    font-weight: 500;
    color: ${WHITE};
    margin-bottom: 4px;
    line-height: 18px;
    word-break: normal;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
`;

const CompactDescription = styled.div`
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    overflow: hidden;
    color: rgba(255, 255, 255, 0.72);
    font-size: 12px;
    line-height: 18px;
    word-break: normal;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
`;

const CompactFooterRow = styled.div`
    display: flex;
    flex-direction: row;
    margin-top: 6px;
    align-items: center;
    gap: 8px;
    width: 100%;
    flex-wrap: wrap;
    overflow: visible;
    min-width: 0;
`;

const CompactLogo = styled.img`
    width: 22px;
    height: 22px;
    border-radius: 5px;
    object-fit: cover;
    flex-shrink: 0;
`;

const CompactFooterText = styled.span`
    color: rgba(255, 255, 255, 0.6);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    max-width: 100%;
    flex: 1;
`;

const CompactReadButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    width: 100%;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 8px;
    padding: 8px 10px;
    margin-top: 10px;
    background: rgba(255, 255, 255, 0.06);
    color: ${WHITE};
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    cursor: pointer;

    &:hover {
        background: rgba(255, 255, 255, 0.1);
        border-color: rgba(255, 255, 255, 0.18);
    }
`;

const ByLine = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    font-size: 11px;
    color: ${PALE_WHITE_6};
`;

const PublisherIcon = styled.img`
    width: 14px;
    height: 14px;
    border-radius: 3px;
    object-fit: cover;
    flex: 0 0 auto;
`;

const AuthorName = styled.span`
    font-weight: 600;
    color: rgba(255, 255, 255, 0.78);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ByLineSeparator = styled.span`
    flex: 0 0 auto;
`;

const SourceLabel = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 0.02em;
`;

const Title = styled.div`
    font-size: 14px;
    font-weight: 600;
    line-height: 1.35;
    color: rgba(255, 255, 255, 0.92);
    overflow-wrap: anywhere;
`;

const Description = styled.div`
    font-size: 12px;
    line-height: 1.45;
    color: rgba(255, 255, 255, 0.64);
    overflow-wrap: anywhere;
    white-space: pre-wrap;

    ${({ $isCompact }) =>
        $isCompact &&
        `
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
        overflow: hidden;
    `}
`;

const MediaVideo = styled.video`
    width: 100%;
    max-height: 360px;
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.3);
    margin-top: 4px;
`;

const MediaAudio = styled.audio`
    width: 100%;
    margin-top: 4px;
`;

const GalleryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 4px;
    margin-top: 4px;
`;

const GalleryItem = styled.div`
    position: relative;
    aspect-ratio: 1;
    border-radius: 6px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.06);
`;

const GalleryImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
`;

const GalleryMoreOverlay = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
    color: rgba(255, 255, 255, 0.9);
    font-size: 13px;
    font-weight: 600;
`;

const ReadButton = styled.button`
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 4px;
    padding: 5px 10px;
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: 7px;
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.85);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;

    &:hover {
        background: rgba(255, 255, 255, 0.12);
    }
`;

export default ReadOnlyUrlMetaPreview;
